import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import ReferenceMap from './ReferenceMap';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../../editor/nodes/PlaygroundNodes';
import { processMathEquation } from '../../components/utils/latexParser';
import PrintPreview from '../../components/printPreview';
import ProblemSelector from './ProblemSelector';
import PreviewSection from './PreviewSection';
import { Search } from 'lucide-react';

function PresetModal({ isOpen, onClose, stamp, onSaveSuccess, isEditMode = false, presetToEdit = null, initialProblems = null, initialPresetName = null, onChangeExamProblems = null }) {
  const { getAccessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [problemCounts, setProblemCounts] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('d4');
  const [showOnlyProblems, setShowOnlyProblems] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [problemList, setProblemList] = useState(null);
  const [selectedProblems, setSelectedProblems] = useState([]);
  const [problemDetails, setProblemDetails] = useState({});
  const [loadingProblemDetails, setLoadingProblemDetails] = useState(false);
  const [printFunction, setPrintFunction] = useState(null);
  const [presetLabel, setPresetLabel] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [presetLabelError, setPresetLabelError] = useState(false);
  const [presetId, setPresetId] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(20);
  
  // 검색 관련 상태 추가
  const [searchTerm, setSearchTerm] = useState(''); // 검색창에 입력된 값
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(''); // 실제 API에 적용된 검색어
  
  // 자동 포커스를 위한 마지막 추가된 문제 ID
  const [lastAddedProblemId, setLastAddedProblemId] = useState(null);
  
  // 문제 교체 기능을 위한 상태 추가
  const [editingProblemId, setEditingProblemId] = useState(null);
  const [editingProblemOriginal, setEditingProblemOriginal] = useState(null);
  const [isProblemEditMode, setIsProblemEditMode] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false); // 교체 중 로딩 상태

  // lastAddedProblemId 리셋을 위한 useEffect
  useEffect(() => {
    if (lastAddedProblemId) {
      // 3초 후 자동으로 리셋 (스크롤 완료 후)
      const timer = setTimeout(() => {
        setLastAddedProblemId(null);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [lastAddedProblemId]);
  
  // 수정 모드일 경우 초기 데이터 로드
  useEffect(() => {
    if (isOpen && isEditMode && presetToEdit && presetToEdit.preset_id) {
      loadPresetForEditing(presetToEdit.preset_id);
    }
  }, [isOpen, isEditMode, presetToEdit]);
  
  // 새 프리셋 생성 모드일 경우 초기 데이터 로드
  useEffect(() => {
    if (isOpen && stamp && stamp.tree_id && !isEditMode) {
      fetchReferenceFields(stamp.tree_id);
      fetchProblemCounts(stamp.tree_id, appliedSearchTerm);
    }
  }, [isOpen, stamp, isEditMode]);

  // 외부에서 전달된 초기 문제 목록 처리 (ExamResult에서 사용)
  useEffect(() => {
    if (isOpen && !isEditMode && initialProblems?.length > 0) {
      const problems = initialProblems.map((p, idx) => ({
        problem_id: p.problem_id,
        address: p.address,
        step: p.step,
        sequence: idx + 1
      }));
      setSelectedProblems(problems);
      const problemIds = problems.map(p => p.problem_id);
      fetchProblemsDetail(problemIds);
    }
  }, [isOpen, isEditMode, initialProblems]);

  // 외부에서 전달된 초기 프리셋 이름 처리
  useEffect(() => {
    if (isOpen && !isEditMode && initialPresetName) {
      setPresetLabel(initialPresetName);
    }
  }, [isOpen, isEditMode, initialPresetName]);

  // 프리셋 수정을 위한 데이터 로드
  const loadPresetForEditing = async (presetId) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${presetId}`, {
        headers: {
          'Authorization': token
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        const presetData = data.data;
        
        // 트리 ID를 사용하여 참조 필드와 문제 개수 로드
        if (presetData.tree_id) {
          fetchReferenceFields(presetData.tree_id);
          fetchProblemCounts(presetData.tree_id);
        }
        
        // 기존 프리셋 정보 설정
        setPresetId(presetData.preset_id);
        setPresetLabel(presetData.preset_name);
        setPresetDescription(presetData.description || '');
        
        // preset_info가 배열인지 확인 후 처리
        let problems = [];
        if (presetData.preset_info && Array.isArray(presetData.preset_info)) {
          problems = presetData.preset_info.map(info => ({
            problem_id: info.problem_id,
            address: info.address,
            step: info.step,
            sequence: info.sequence
          }));
        } else {
          console.error('preset_info가 배열이 아닙니다:', presetData.preset_info);
        }
        
        setSelectedProblems(problems);
        
        // 원본 데이터 저장
        setOriginalData({
          preset_name: presetData.preset_name,
          description: presetData.description || '',
          problems: [...problems]
        });
        setHasChanges(false);
        
        // 문제 상세 정보 로드
        if (problems.length > 0) {
          const problemIds = problems.map(p => p.problem_id);
          fetchProblemsDetail(problemIds);
        }
        
      } else {
        throw new Error('데이터 형식이 올바르지 않습니다.');
      }
    } catch (error) {
      console.error('시험지 데이터 로드 오류:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 여러 문제의 상세 정보를 한번에 가져오는 함수 (모든 페이지 자동 로딩)
  const fetchProblemsDetail = async (problemIds) => {
    if (!problemIds || problemIds.length === 0) return;

    setLoadingProblemDetails(true);
    try {
      const token = await getAccessToken();
      const problemIdsString = problemIds.join(',');
      const totalPages = Math.ceil(problemIds.length / perPage) || 1;

      for (let page = 1; page <= totalPages; page++) {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/problems/print/page?problem_ids=${problemIdsString}&page=${page}&per_page=${perPage}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          }
        );

        if (response.status === 204) break;

        if (!response.ok) {
          throw new Error(`문제 상세 정보 로드 실패 (상태 코드: ${response.status})`);
        }

        const data = await response.json();

        // 상세 정보 매핑 처리
        const details = {};
        data.data.forEach(item => {
          if (item.lexical_data && item.lexical_data.lexical_data) {
            const content = convertLexicalToHtml(item.lexical_data.lexical_data);
            details[item.problem_id] = { content };
          } else {
            details[item.problem_id] = { content: '문제 내용을 불러올 수 없습니다.' };
          }
        });

        setProblemDetails(prev => ({ ...prev, ...details }));
      }
    } catch (error) {
      console.error('문제 상세 정보 로드 실패:', error);
    } finally {
      setLoadingProblemDetails(false);
    }
  };

  const fetchReferenceFields = async (treeId) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/field/${treeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        setMapData(data.data);
      } else {
        throw new Error('데이터 형식이 올바르지 않습니다.');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProblemCounts = async (treeId, searchLabels = '') => {
    try {
      const token = await getAccessToken();
      
      // URL 파라미터 구성
      let url = `${process.env.REACT_APP_API_BASE_URL}/api/referencefields/${treeId}/count/problems`;
      if (searchLabels) {
        url += `?search_labels=${encodeURIComponent(searchLabels)}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.message || '검색어가 올바르지 않습니다.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        setProblemCounts(data.data);
      } else {
        throw new Error('문제 수 데이터 형식이 올바르지 않습니다.');
      }
    } catch (error) {
      console.error('문제 수 조회 오류:', error);
      setError(error.message);
    }
  };

  // 검색어 초기화 함수
  const resetSearch = () => {
    // 교체 모드 취소 로직 제거 - 사용자가 교체 모드에서 검색을 초기화할 수 있어야 함
    
    setSearchTerm('');
    // 깜박임 방지: problemCounts를 먼저 null로 설정
    setProblemCounts(null);
    setAppliedSearchTerm('');
    // 검색어 초기화 시 첫 번째 API 호출하고 두 번째 화면 비우기
    if (stamp && stamp.tree_id) {
      fetchProblemCounts(stamp.tree_id, '');
      setSelectedCell(null);
      setProblemList(null);
      setProblemDetails({});
    }
  };

  // 검색 실행 함수
  const executeSearch = () => {
    if (searchTerm.length >= 100) {
      alert('검색어는 100자 미만이어야 합니다.');
      return;
    }
    
    // 교체 모드 취소 로직 제거 - 사용자가 교체 모드에서 검색할 수 있어야 함
    
    // 검색어 변경 시 첫 번째 API 호출하고 두 번째 화면 비우기
    if (stamp && stamp.tree_id) {
      // 깜박임 방지: problemCounts를 먼저 null로 설정하여 이전 데이터 제거
      setProblemCounts(null);
      setAppliedSearchTerm(searchTerm);
      fetchProblemCounts(stamp.tree_id, searchTerm);
      setSelectedCell(null);
      setProblemList(null);
      setProblemDetails({});
    }
  };

  // 검색창 키 이벤트 핸들러
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      resetSearch();
    }
  };

  // ESC 키 이벤트 리스너 (전역)
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        // 우선순위 1: 문제 교체 모드가 활성화되어 있으면 취소
        if (isProblemEditMode) {
          cancelEditing();
          return;
        }
        
        // 우선순위 2: 검색창이 포커스되어 있지 않을 때만 검색 초기화
        if (document.activeElement?.id !== 'search-input' && document.activeElement?.id !== 'search-input-desktop') {
          resetSearch();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, isProblemEditMode]);

  // 맵에서 셀 클릭 처리 핸들러
  const handleCellClick = (cellInfo) => {
    // 교체 모드 취소 로직 제거 - 사용자가 교체 모드에서 유형을 선택할 수 있어야 함
    
    // 문제 상세 정보 및 상태 초기화
    setProblemDetails({});
    setSelectedCell(cellInfo);
    setCurrentPage(1); // 새로운 셀 클릭 시 페이지를 1로 초기화
    
    if (cellInfo && stamp && stamp.tree_id) {
      fetchAllProblemList(stamp.tree_id, cellInfo.address, cellInfo.step, appliedSearchTerm);
    }
  };

  // 전체 문제 목록을 가져오는 함수 (페이지 매개변수 없음)
  const fetchAllProblemList = async (treeId, address, step, searchLabels = '') => {
    try {
      const token = await getAccessToken();
      
      // URL 파라미터 구성
      let url = `${process.env.REACT_APP_API_BASE_URL}/api/referencefields/${treeId}/problems?address=${address}&step=${step}`;
      if (searchLabels) {
        url += `&search_labels=${encodeURIComponent(searchLabels)}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.message || '검색어가 올바르지 않습니다.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        // 전체 문제 목록 저장 (problem_id 기준 내림차순 정렬)
        const sortedData = {
          ...data.data,
          problems: [...data.data.problems].sort((a, b) => b.problem_id - a.problem_id)
        };
        setProblemList(sortedData);
        
        // 첫 페이지 문제 ID 추출 (최대 perPage개)
        const firstPageProblemIds = sortedData.problems
          .slice(0, perPage)
          .map(p => p.problem_id);
        
        // 첫 페이지 문제 상세 정보 요청
        if (firstPageProblemIds.length > 0) {
          const problemIdsString = firstPageProblemIds.join(',');
          fetchProblemDetails(problemIdsString);
        }
        
        // 전체 페이지 수 계산
        const totalPagesCount = Math.ceil(sortedData.problems.length / perPage);
        setTotalPages(totalPagesCount);
      } else {
        throw new Error('문제 리스트 데이터 형식이 올바르지 않습니다.');
      }
    } catch (error) {
      console.error('문제 리스트 조회 오류:', error);
      setError(error.message);
    }
  };

  // 기존의 fetchProblemList 함수와 fetchProblemsDetail 함수를 사용하지 않도록 수정
  // 완전히 새로운 함수로 대체
  const fetchProblemDetails = async (problemIdsString) => {
    if (!problemIdsString) return;
    
    setLoadingProblemDetails(true);
    try {
      const token = await getAccessToken();
      
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/problems/print/page?problem_ids=${problemIdsString}&page=1&per_page=${perPage}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      
      if (response.status === 204) {
        console.warn('데이터 없음');
        return;
      }
      
      if (!response.ok) {
        throw new Error(`API 요청 실패 (상태 코드: ${response.status})`);
      }

      const data = await response.json();
      
      // 상세 정보 매핑 처리
      const details = {};
      if (data.data && Array.isArray(data.data)) {
        data.data.forEach(item => {
          if (item.lexical_data && item.lexical_data.lexical_data) {
            // Lexical 데이터를 HTML로 변환
            const content = convertLexicalToHtml(item.lexical_data.lexical_data);
            details[item.problem_id] = { content };
          } else {
            details[item.problem_id] = { content: '문제 내용을 불러올 수 없습니다.' };
          }
        });
      }
      
      // 이전 상세 정보를 완전히 대체 (초기화 후 새 데이터 설정)
      setProblemDetails(details);
    } catch (error) {
      console.error('문제 상세 정보 로드 실패:', error);
    } finally {
      setLoadingProblemDetails(false);
    }
  };
  
  const convertLexicalToHtml = useCallback((lexicalData) => {
    const editor = createEditor({
      nodes: [...PlaygroundNodes]
    });

    const processNode = (node) => {
      if (node.type === 'equation') {
        node.equation = processMathEquation(node.equation);
      } else if (node.type === 'box-content') {
        node.className = 'PlaygroundEditorTheme__boxContent';
      }
      if (node.children && Array.isArray(node.children)) {
        node.children = node.children.map(child => processNode(child));
      }
      return node;
    };

    lexicalData.root = processNode(lexicalData.root);
    const editorState = editor.parseEditorState(lexicalData);

    let content = '';
    editor.setEditorState(editorState);
    editor.update(() => {
      content = $generateHtmlFromNodes(editor);
    });

    return content;
  }, []);
  
  // 미리보기에서 문제 클릭 처리 (항상 교체 모드로 진입)
  const handlePreviewProblemClick = (problem) => {
    if (editingProblemId === problem.problem_id) {
      // 같은 문제 클릭 시 수정 모드 해제
      cancelEditing();
    } else {
      // 새로운 문제 수정 모드 진입
      startEditing(problem);
    }
  };

  // ProblemSelector에서 문제 클릭 처리
  const handleSelectorProblemClick = (problem) => {
    // 문제 교체 모드인 경우
    if (isProblemEditMode) {
      // 교체 처리
      handleProblemReplace(problem);
      return;
    }

    // 출제 시험만 보기 모드에서 이미 선택된 문제 클릭 시 → 미리보기 포커싱 + 셀 하이라이팅
    if (showOnlyProblems && selectedProblems.some(p => p.problem_id === problem.problem_id)) {
      setLastAddedProblemId(null);
      setTimeout(() => setLastAddedProblemId(problem.problem_id), 0);
      setSelectedCell({ address: problem.address, step: problem.step });
      return;
    }

    // 기존 로직: 문제 추가/제거
    setSelectedProblems(prev => {
      if (prev.some(p => p.problem_id === problem.problem_id)) {
        // 문제 제거
        setLastAddedProblemId(null);
        return prev.filter(p => p.problem_id !== problem.problem_id);
      }
      // 문제 추가
      setLastAddedProblemId(problem.problem_id);
      return [...prev, problem];
    });
  };

  // 문제 수정 모드 시작
  const startEditing = (problem) => {
    setEditingProblemId(problem.problem_id);
    setEditingProblemOriginal(problem);
    setIsProblemEditMode(true);
  };

  // 문제 교체 처리
  const handleProblemReplace = (newProblem) => {
    if (!editingProblemId || !newProblem || isReplacing) {
      return; // 이미 교체 중이면 무시
    }
    
    setIsReplacing(true); // 교체 시작
    
    try {
      // 0. 선택된 문제가 없는 경우 - 에러 상황
      if (!selectedProblems || selectedProblems.length === 0) {
        alert('교체할 문제가 없습니다.');
        cancelEditing();
        return;
      }
      
      // 1. 교체할 대상 문제가 실제로 존재하는지 확인
      const targetProblem = selectedProblems.find(p => p.problem_id === editingProblemId);
      if (!targetProblem) {
        alert('교체할 문제를 찾을 수 없습니다.');
        cancelEditing();
        return;
      }
      
      // 2. 같은 문제로 교체하려는 경우 (자기 자신으로 교체) - 아무것도 하지 않음
      if (editingProblemId === newProblem.problem_id) {
        cancelEditing();
        return;
      }
      
      // 3. 이미 선택된 문제로 교체하려는 경우 - 중복 방지
      const isDuplicate = selectedProblems.some(p => 
        p.problem_id === newProblem.problem_id && p.problem_id !== editingProblemId
      );
      
      if (isDuplicate) {
        alert('이미 선택된 문제입니다. 다른 문제를 선택해주세요.');
        return; // 교체 모드는 유지
      }
      
      // 4. 정상적인 교체 처리
      setSelectedProblems(prev => 
        prev.map(p => {
          if (p.problem_id === editingProblemId) {
            // 기존 문제의 sequence를 유지하면서 새 문제로 교체
            return {
              ...newProblem,
              sequence: p.sequence
            };
          }
          return p;
        })
      );
      
      // 교체 완료 후 수정 모드 해제
      cancelEditing();
    } finally {
      setIsReplacing(false); // 교체 상태 해제
    }
  };

  // 문제 순서 변경 핸들러
  const handleReorderProblem = (problemId, direction) => {
    setSelectedProblems(prev => {
      const index = prev.findIndex(p => p.problem_id === problemId);
      if (index === -1) return prev;
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const newArr = [...prev];
      [newArr[index], newArr[targetIndex]] = [newArr[targetIndex], newArr[index]];
      return newArr;
    });
  };

  // 문제 제거 핸들러
  const handleRemoveProblem = (problemId) => {
    setSelectedProblems(prev => prev.filter(p => p.problem_id !== problemId));
  };

  // 수정 모드 취소
  const cancelEditing = () => {
    setEditingProblemId(null);
    setEditingProblemOriginal(null);
    setIsProblemEditMode(false);
    setIsReplacing(false); // 교체 중 상태도 초기화
  };

  const handlePrint = () => {
    if (printFunction) {
      printFunction();
    }
  };

  const handleCancel = () => {
    // 수정 모드이고 변경 사항이 있으면 확인 창 표시
    if (isEditMode && hasChanges) {
      const isConfirmed = window.confirm('변경된 내역을 저장하지 않고 종료하시겠습니까?');
      if (!isConfirmed) {
        return; // 사용자가 취소를 선택하면 모달을 닫지 않음
      }
    }
    
    // 초기화 작업
    setSelectedCell(null);
    setProblemList(null);
    setSelectedProblems([]);
    setProblemDetails({});
    setPresetLabel('');
    setPresetDescription('');
    setOriginalData(null);
    setHasChanges(false);
    
    // 문제 교체 모드 초기화
    setEditingProblemId(null);
    setEditingProblemOriginal(null);
    setIsProblemEditMode(false);

    onClose();
  };

  // 프리셋 이름 중복 확인 — 중복이면 해당 프리셋 객체 반환, 없으면 null
  const findPresetByName = async (name, token) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/mypresets`,
        { headers: { 'Authorization': token, 'Content-Type': 'application/json' } }
      );
      if (!response.ok) return null;
      const data = await response.json();
      const presets = data.data || [];
      return presets.find(p => p.preset_name === name.trim()) || null;
    } catch {
      return null;
    }
  };

  const handleSave = async () => {
    if ((!stamp && !isEditMode) || selectedProblems.length === 0) {
      return;
    }
    
    // 시험지명이 비어있는지 확인
    if (!presetLabel.trim()) {
      setPresetLabelError(true);
      return;
    }
    
    // preset_name에 금지된 문자 포함 여부 확인
    const invalidChars = /["'\\\/=]/;
    if (invalidChars.test(presetLabel)) {
      alert("시험지명에는 따옴표, 작은따옴표, 백슬래시, 슬래시, 등호를 사용할 수 없습니다.");
      return;
    }
    
    // 문제 수 검증
    if (selectedProblems.length >= 250) {
      alert("문제는 250개 미만으로 선택해야 합니다.");
      return;
    }
    
    // preset_info 배열 생성
    const presetInfo = selectedProblems.map((problem, index) => ({
      address: problem.address,
      step: problem.step,
      sequence: index + 1,
      problem_id: problem.problem_id
    }));
    
    // 최종 데이터 구조 생성
    const presetData = {
      preset_name: presetLabel,
      description: presetDescription,
      preset_info: presetInfo
    };
    
    try {
      setIsLoading(true);
      const token = await getAccessToken();

      // 생성 모드일 때 이름 중복 확인
      if (!isEditMode) {
        const existing = await findPresetByName(presetLabel, token);
        if (existing) {
          alert(`동일한 이름의 시험지(커스텀)가 이미 존재합니다: "${presetLabel.trim()}"`);
          setIsLoading(false);
          return;
        }
      }

      let url, method;

      if (isEditMode && presetId) {
        // 수정 모드
        url = `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${presetId}`;
        method = 'PUT';
      } else {
        // 생성 모드
        url = `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/mypresets`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(presetData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `서버 오류가 발생했습니다. (상태 코드: ${response.status})`);
      }

      const data = await response.json();
      alert(isEditMode ? "시험지가 성공적으로 수정되었습니다." : "시험지가 성공적으로 저장되었습니다.");
      
      // 저장 성공 시 콜백 함수 호출
      if (onSaveSuccess) {
        onSaveSuccess(data);
      }
      
      // handleCancel() 호출 대신 직접 초기화 및 모달 닫기 처리
      setSelectedCell(null);
      setProblemList(null);
      setSelectedProblems([]);
      setProblemDetails({});
      setPresetLabel('');
      setPresetDescription('');
      setOriginalData(null);
      setHasChanges(false);

      // 문제 교체 모드 초기화
      setEditingProblemId(null);
      setEditingProblemOriginal(null);
      setIsProblemEditMode(false);

      onClose();
    } catch (error) {
      console.error(isEditMode ? "시험지 수정 오류:" : "시험지 저장 오류:", error);
      alert(`${isEditMode ? "시험지 수정" : "시험지 저장"} 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 출제변경: 프리셋 저장 후 onChangeExamProblems 콜백 호출
  const handleChangeExam = async () => {
    if ((!stamp && !isEditMode) || selectedProblems.length === 0) {
      return;
    }

    if (!presetLabel.trim()) {
      setPresetLabelError(true);
      return;
    }

    const invalidChars = /["'\\\/=]/;
    if (invalidChars.test(presetLabel)) {
      alert("시험지명에는 따옴표, 작은따옴표, 백슬래시, 슬래시, 등호를 사용할 수 없습니다.");
      return;
    }

    if (selectedProblems.length >= 250) {
      alert("문제는 250개 미만으로 선택해야 합니다.");
      return;
    }

    const presetInfo = selectedProblems.map((problem, index) => ({
      address: problem.address,
      step: problem.step,
      sequence: index + 1,
      problem_id: problem.problem_id
    }));

    const presetData = {
      preset_name: presetLabel,
      description: presetDescription,
      preset_info: presetInfo
    };

    try {
      setIsLoading(true);
      const token = await getAccessToken();

      let targetPresetId;

      // 동일 이름 프리셋이 있으면 기존 것을 사용, 없으면 새로 생성
      const existing = await findPresetByName(presetLabel, token);
      if (existing) {
        if (!window.confirm(`동일한 이름의 시험지(커스텀)가 이미 존재합니다: "${presetLabel.trim()}"\n기존 시험지로 출제변경하시겠습니까?`)) {
          setIsLoading(false);
          return;
        }
        targetPresetId = existing.preset_id;
      } else {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/mypresets`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(presetData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `서버 오류가 발생했습니다. (상태 코드: ${response.status})`);
        }

        const data = await response.json();
        targetPresetId = data?.preset_id || data?.data?.preset_id;

        // preset_id를 응답에서 못 받은 경우 이름으로 재조회 (fallback)
        if (!targetPresetId) {
          const found = await findPresetByName(presetLabel, token);
          targetPresetId = found?.preset_id;
        }

        if (!targetPresetId) {
          alert('프리셋 저장은 완료되었으나 ID를 받지 못했습니다.');
          return;
        }
      }

      // 출제변경 콜백을 먼저 호출 (모달 닫기 전에 실행해야 confirm 등이 정상 동작)
      const result = await onChangeExamProblems(targetPresetId);

      // 사용자가 confirm에서 취소했거나 실패한 경우 모달을 닫지 않음
      if (result === false) return;

      // 모달 초기화 + 닫기
      setSelectedCell(null);
      setProblemList(null);
      setSelectedProblems([]);
      setProblemDetails({});
      setPresetLabel('');
      setPresetDescription('');
      setOriginalData(null);
      setHasChanges(false);
      setEditingProblemId(null);
      setEditingProblemOriginal(null);
      setIsProblemEditMode(false);
      onClose();
    } catch (error) {
      console.error("시험지 저장 오류:", error);
      alert(`시험지 저장 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 시험지명 입력 처리 함수
  const handlePresetLabelChange = (e) => {
    setPresetLabel(e.target.value);
    // 값이 입력되면 오류 상태 해제
    if (e.target.value.trim()) {
      setPresetLabelError(false);
    }
  };

  // 데이터 변경 감지를 위한 useEffect
  useEffect(() => {
    if (isEditMode && originalData) {
      const isNameChanged = presetLabel !== originalData.preset_name;
      const isDescriptionChanged = presetDescription !== originalData.description;
      
      // 문제 배열 비교
      const areProblemsChanged = () => {
        if (selectedProblems.length !== originalData.problems.length) return true;
        
        // problem_id 기준으로 정렬된 배열 생성
        const sortedOriginal = [...originalData.problems].sort((a, b) => a.problem_id - b.problem_id);
        const sortedCurrent = [...selectedProblems].sort((a, b) => a.problem_id - b.problem_id);
        
        // 각 요소 비교
        for (let i = 0; i < sortedOriginal.length; i++) {
          if (sortedOriginal[i].problem_id !== sortedCurrent[i].problem_id) return true;
        }
        
        return false;
      };
      
      setHasChanges(isNameChanged || isDescriptionChanged || areProblemsChanged());
    }
  }, [isEditMode, originalData, presetLabel, presetDescription, selectedProblems]);

  // 페이지 변경 핸들러 수정
  const handlePageChange = (newPage) => {
    
    if (newPage >= 1 && newPage <= totalPages) {
      // 페이지 변경 시 problemDetails 초기화 (로딩 중 상태 표시를 위해)
      setProblemDetails({});
      setCurrentPage(newPage);
      
      // 문제 목록이 있을 경우 해당 페이지의 문제 상세 정보 다시 로드
      if (problemList && problemList.problems) {
        // 전체 문제 목록에서 페이지에 해당하는 부분 추출
        const startIndex = (newPage - 1) * perPage;
        const endIndex = Math.min(startIndex + perPage, problemList.problems.length);
        
        
        // 현재 페이지의 문제 ID만 추출
        const currentPageProblemIds = problemList.problems
          .slice(startIndex, endIndex)
          .map(p => p.problem_id);
        
        
        // 페이지에 표시될 문제 ID만 사용하여 상세 정보 요청
        if (currentPageProblemIds.length > 0) {
          // 문제 ID 목록 문자열로 변환
          const problemIdsString = currentPageProblemIds.join(',');
          fetchProblemDetails(problemIdsString);
        }
      }
    }
  };

  if (!isOpen) return null;

  const isMobileView = window.innerWidth < 768;

  return createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center" 
      style={{ 
        zIndex: 9999980, // 기존 Modal 컴포넌트와 동일한 zIndex
        paddingTop: isMobileView ? 'env(safe-area-inset-top, 0px)' : '0px',
        paddingBottom: isMobileView ? 'calc(68px + env(safe-area-inset-bottom, 0px))' : '0px',
        backgroundColor: 'rgba(0, 0, 0, 0.3)'
      }}
    >
      <div 
        className="flex flex-col rounded-3xl bg-white shadow-xl w-[95vw] lg:w-[95vw]"
        style={{
          height: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh',
          maxHeight: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh'
        }}
      >
        {/* 모달 헤더 */}
        <div className="flex shrink-0 items-center justify-between border-b p-4">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-bold text-gray-800">
              {!isLoading && mapData?.field_info?.tree_name 
                ? `${isEditMode ? '시험지 수정' : '시험지 생성'} - ${mapData.field_info.tree_name}` 
                : isEditMode ? "시험지 수정" : "시험지 생성"}
              {isEditMode && <span className="ml-2 rounded-3xl bg-yellow-100 px-2 py-1 text-xs text-yellow-800">수정모드</span>}
            </h2>
          </div>
        </div>
        
        {/* 교체 모드 안내 배너 */}
        {isProblemEditMode && (
          <div className="shrink-0 border-b bg-green-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="flex size-6 items-center justify-center rounded-full bg-green-500 text-white">
                  {isReplacing ? (
                    <div className="size-3 animate-spin rounded-full border border-white border-t-transparent"></div>
                  ) : (
                    <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-green-800">
                    {isReplacing ? '문제 교체 중...' : '문제 교체 모드'}
                  </p>
                  <p className="text-xs text-green-600">
                    {isReplacing 
                      ? '문제를 교체하는 중입니다...'
                      : '교체할 새로운 문제를 선택하거나 ESC 키를 눌러 취소하세요'
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={isReplacing ? undefined : cancelEditing}
                disabled={isReplacing}
                className={`focus:outline-none ${
                  isReplacing 
                    ? 'cursor-not-allowed text-gray-400' 
                    : 'text-green-600 hover:text-green-800'
                }`}
                title={isReplacing ? '교체 중입니다...' : '교체 모드 취소'}
              >
                <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        {/* 모달 바디 */}
        <div className={`flex-1 ${isMobileView ? 'overflow-y-auto' : 'overflow-hidden'}`}>
          {isMobileView ? (
            /* 모바일 레이아웃 */
            <div className="flex h-full flex-col">
              {/* 1행 - 시험지 미리보기 영역 (3/10) */}
              <div className="h-[30%] border-b">
                <div className="h-full overflow-auto p-4">
                  <PreviewSection
                    selectedProblems={selectedProblems}
                    problemDetails={problemDetails}
                    mapData={mapData}
                    stamp={stamp}
                    onProblemClick={handlePreviewProblemClick}
                    setPrintFunction={setPrintFunction}
                    lastAddedProblemId={lastAddedProblemId}
                    editingProblemId={editingProblemId}
                    isLoadingMore={loadingProblemDetails}
                  />
                </div>
              </div>

              {/* 2행 - 문제 선택 영역 (3/10) */}
              <div className="h-[30%] border-b">
                <div className="h-full overflow-hidden p-4 flex">
                  <button
                    onClick={() => setShowOnlyProblems(prev => !prev)}
                    className={`shrink-0 mr-2 rounded-r-xl px-1.5 py-2 text-xs font-medium transition-colors ${
                      showOnlyProblems
                        ? 'bg-primary text-white'
                        : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    style={{ writingMode: 'vertical-rl' }}
                    disabled={selectedProblems.length === 0}
                  >
                    출제 시험만 보기 ({selectedProblems.length})
                  </button>
                  <div className="flex-1 min-w-0">
                  <ProblemSelector
                    selectedCell={selectedCell}
                    problemList={problemList}
                    problemDetails={problemDetails}
                    loadingProblemDetails={loadingProblemDetails}
                    selectedProblems={selectedProblems}
                    onProblemClick={handleSelectorProblemClick}
                    onReorderProblem={handleReorderProblem}
                    onRemoveProblem={handleRemoveProblem}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    isMobileView={true}
                    isProblemEditMode={isProblemEditMode}
                    searchTerm={appliedSearchTerm}
                    showOnlySelected={showOnlyProblems}
                  />
                  </div>
                </div>
              </div>

              {/* 3행 - 유형 선택 영역 (4/10) */}
              <div className="h-[40%]">
                <div className="h-full overflow-hidden p-4">
                  {/* 검색창 */}
                  <div className="mb-3 flex items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        id="search-input"
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        className="w-full rounded-3xl border border-gray-300 px-3 py-2 pl-9 text-sm shadow-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        placeholder="키워드 검색(','콤마로 분류)"
                        maxLength={99}
                      />
                      <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                    </div>
                    <button
                      onClick={resetSearch}
                      className="shrink-0 rounded-3xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500/20"
                    >
                      초기화
                    </button>
                    <button
                      onClick={executeSearch}
                      className="shrink-0 rounded-3xl bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                      검색
                    </button>
                  </div>
                  
                  {isLoading && <p className="py-4 text-center text-gray-600">로딩 중...</p>}
                  
                  {error && (
                    <div className="mb-3 rounded border border-red-300 bg-red-50 p-3 text-red-700">
                      <p className="font-semibold">오류 발생</p>
                      <p className="text-sm">{error}</p>
                    </div>
                  )}
                  
                                      {!isLoading && !error && mapData && (
                      <ReferenceMap
                        mapData={mapData}
                        problemCounts={problemCounts}
                        selectedLevel={selectedLevel}
                        selectedCell={selectedCell}
                        selectedProblems={selectedProblems}
                        onLevelChange={setSelectedLevel}
                        onCellClick={handleCellClick}
                        isMobileView={false}
                        hasSearchTerm={!!appliedSearchTerm}
                        searchTerm={appliedSearchTerm}
                      />
                    )}
                </div>
              </div>
            </div>
          ) : (
            /* 데스크톱 레이아웃 */
            <div className="flex h-full">
              {/* 왼쪽 1/4 영역 - 맵 테이블 */}
              <div className="w-1/3 border-r p-4">
                {/* 검색창 */}
                <div className="mb-3 flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      id="search-input-desktop"
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      className="w-full rounded-3xl border border-gray-300 px-3 py-2 pl-9 text-sm shadow-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      placeholder="키워드 검색(','콤마로 분류)"
                      maxLength={99}
                    />
                    <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                  </div>
                  <button
                    onClick={resetSearch}
                    className="shrink-0 rounded-3xl border border-gray-300 bg-white px-2 py-2 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500/20"
                  >
                    초기화
                  </button>
                  <button
                    onClick={executeSearch}
                    className="shrink-0 rounded-3xl bg-primary px-3 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    검색
                  </button>
                </div>
                
                <div className="flex h-full min-h-0 flex-1 flex-col rounded-3xl bg-white shadow-lg">
                  <div className="flex-1 overflow-y-auto bg-gray-50 p-3" style={{ height: 'calc(100% - 2rem)' }}>
                    {isLoading && <p className="py-4 text-center text-gray-600">로딩 중...</p>}
                    
                    {error && (
                      <div className="mb-3 rounded border border-red-300 bg-red-50 p-3 text-red-700">
                        <p className="font-semibold">오류 발생</p>
                        <p className="text-sm">{error}</p>
                      </div>
                    )}
                    
                    {!isLoading && !error && mapData && (
                      <ReferenceMap
                        mapData={mapData}
                        problemCounts={problemCounts}
                        selectedLevel={selectedLevel}
                        selectedCell={selectedCell}
                        selectedProblems={selectedProblems}
                        onLevelChange={setSelectedLevel}
                        onCellClick={handleCellClick}
                        isMobileView={false}
                        hasSearchTerm={!!appliedSearchTerm}
                        searchTerm={appliedSearchTerm}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* 중간 1/4 영역 - 문제 선택기 */}
              <div className="w-1/4 border-r p-4 flex flex-col overflow-hidden">
                <button
                  onClick={() => setShowOnlyProblems(prev => !prev)}
                  className={`mb-2 rounded-3xl px-3 py-1.5 text-xs font-medium transition-colors ${
                    showOnlyProblems
                      ? 'bg-primary text-white'
                      : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  disabled={selectedProblems.length === 0}
                >
                  출제 시험만 보기 ({selectedProblems.length})
                </button>
                <ProblemSelector
                  selectedCell={selectedCell}
                  problemList={problemList}
                  problemDetails={problemDetails}
                  loadingProblemDetails={loadingProblemDetails}
                  selectedProblems={selectedProblems}
                  onProblemClick={handleSelectorProblemClick}
                  onReorderProblem={handleReorderProblem}
                  onRemoveProblem={handleRemoveProblem}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  isMobileView={false}
                  isProblemEditMode={isProblemEditMode}
                  searchTerm={appliedSearchTerm}
                  showOnlySelected={showOnlyProblems}
                />
              </div>

                          {/* 오른쪽 2/4 영역 - 프리뷰 */}
            <div className="w-2/4 p-4 flex flex-col overflow-auto" style={{ height: 'calc(100% - 2rem)' }}>
              <PreviewSection
                selectedProblems={selectedProblems}
                problemDetails={problemDetails}
                mapData={mapData}
                stamp={stamp}
                onProblemClick={handlePreviewProblemClick}
                setPrintFunction={setPrintFunction}
                lastAddedProblemId={lastAddedProblemId}
                editingProblemId={editingProblemId}
                isLoadingMore={loadingProblemDetails}
              />
            </div>
            </div>
          )}
        </div>
        
        {/* 모달 푸터 */}
        <div className="flex shrink-0 items-center justify-between border-t p-4 min-w-0">
          {/* 좌측: 시험지명과 설명 입력 */}
          <div className="flex items-center space-x-4 flex-1 min-w-0 mr-4">
            <div className="flex items-center min-w-0 flex-shrink">
              <input
                id="preset-label"
                type="text"
                value={presetLabel}
                onChange={handlePresetLabelChange}
                className={`${isMobileView ? 'w-[120px] min-w-[100px]' : 'w-[250px] min-w-[200px]'} border ${presetLabelError ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-3xl px-3 py-1.5 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm placeholder:text-xs`}
                placeholder="시험지명 (필수, 최대 100자)"
                maxLength={100}
              />
            </div>
            
            <div className="flex items-center min-w-0 flex-shrink">
              <input
                id="preset-description"
                type="text"
                value={presetDescription}
                onChange={(e) => setPresetDescription(e.target.value)}
                className={`${isMobileView ? 'w-[120px] min-w-[100px]' : 'w-[250px] min-w-[200px]'} rounded-3xl border border-gray-300 px-3 py-1.5 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm placeholder:text-xs`}
                placeholder="설명 (옵션, 최대 500자)"
                maxLength={500}
              />
            </div>
          </div>
          
          {/* 우측: 버튼들 (고정 폭 유지) */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleCancel}
              className="rounded-3xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap"
            >
              취소
            </button>
            {onChangeExamProblems && (
              <button
                onClick={handleChangeExam}
                className="rounded-3xl bg-orange-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400 whitespace-nowrap"
                disabled={selectedProblems.length === 0}
              >
                시험지출제변경
              </button>
            )}
            <button
              onClick={handleSave}
              className="rounded-3xl bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400 whitespace-nowrap"
              disabled={selectedProblems.length === 0}
            >
              {isEditMode ? '수정' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PresetModal;
